import type { FC, ComponentType, SVGProps } from 'react';
import { motion } from 'motion/react';
import classNames from 'classnames';

type BadgeVariant = 'revenues' | 'expenses' | 'taxes';

interface IntegrationBadgeProps {
  variant: BadgeVariant;
  label: string;
  Icon: ComponentType<SVGProps<SVGSVGElement> | { className?: string }>;
  delay?: number;
}

const VARIANT_STYLES: Record<
  BadgeVariant,
  { bg: string; text: string; shadow: string }
> = {
  revenues: {
    bg: 'bg-[#e5f7d9]',
    text: 'text-[#366e1c]',
    shadow:
      'shadow-[0px_1px_2px_0px_rgba(21,49,12,0.1),0px_0px_0px_1px_#e5f7d9]',
  },
  expenses: {
    bg: 'bg-[#fbeaf6]',
    text: 'text-[#ac2073]',
    shadow:
      'shadow-[0px_1px_2px_0px_rgba(41,21,71,0.1),0px_0px_0px_1px_#fbeaf6]',
  },
  taxes: {
    bg: 'bg-[#eeeafb]',
    text: 'text-[#7431d2]',
    shadow:
      'shadow-[0px_1px_2px_0px_rgba(41,21,71,0.1),0px_0px_0px_1px_#eeeafb]',
  },
};

const IntegrationBadge: FC<IntegrationBadgeProps> = ({
  variant,
  label,
  Icon,
  delay = 0,
}) => {
  const styles = VARIANT_STYLES[variant];

  return (
    <motion.div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-sm ${styles.bg} ${styles.shadow}`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay, ease: [0, 0, 0.2, 1] }}
    >
      <div
        className={classNames('flex items-center justify-center', styles.text)}
      >
        <Icon className="w-7 h-7" />
      </div>
      <span className={classNames('text-sm font-semibold', styles.text)}>
        {label}
      </span>
    </motion.div>
  );
};

export default IntegrationBadge;
